import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import useEventMode from '@/hooks/useEventMode';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendUp, Check, ArrowRight, Lightning, Ticket, X
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const { enabled: eventMode } = useEventMode();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  // Coupon state — applies to the next purchase
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount_type, original_price, discount_amount, final_price, package_id}
  const [couponLoading, setCouponLoading] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/packages`);
      return response.data;
    } catch (error) {
      toast.error('Failed to load packages');
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchPackages().then((data) => {
      if (!mounted) return;
      if (data) setPackages(data);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, [fetchPackages]);

  const handleValidateCoupon = async (pkg) => {
    if (!couponCode.trim()) return;
    if (!isAuthenticated) { navigate('/register'); return; }
    setCouponLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/coupons/validate`,
        { code: couponCode.trim(), package_id: pkg.id },
        { headers: getAuthHeaders() }
      );
      if (res.data.valid) {
        setAppliedCoupon({ ...res.data, package_id: pkg.id });
        toast.success(t('pricing.coupon.applied'));
      } else {
        setAppliedCoupon(null);
        toast.error(res.data.error || t('pricing.coupon.invalid'));
      }
    } catch (err) {
      setAppliedCoupon(null);
      toast.error(err.response?.data?.detail || t('pricing.coupon.invalid'));
    } finally { setCouponLoading(false); }
  };

  const clearCoupon = () => { setCouponCode(''); setAppliedCoupon(null); };

  const handlePurchase = async (pkg) => {
    if (!isAuthenticated) { navigate('/register'); return; }
    // Apply coupon ONLY if it was validated for THIS package
    const couponToUse = appliedCoupon && appliedCoupon.package_id === pkg.id ? appliedCoupon.code : null;
    if (pkg.price === 0 && !couponToUse) {
      toast.success(t('pricing.start_free'));
      navigate('/companies');
      return;
    }
    // Paid purchase requires legal consent (Türkiye Mesafeli Satış zorunluluğu)
    if (!legalAccepted) {
      toast.error(t('pricing.legal.required'));
      const el = document.getElementById('legal-consent');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setPurchasing(pkg.id);
    try {
      const response = await axios.post(`${API_URL}/payments/create-checkout`, {
        package_id: pkg.id,
        coupon_code: couponToUse,
        legal_consent_accepted: legalAccepted,
        success_url: `${window.location.origin}/dashboard?payment=success`,
        cancel_url: `${window.location.origin}/pricing?payment=cancelled`
      }, { headers: getAuthHeaders() });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        // Coupon made it free / free package → instant activation
        toast.success(response.data.message || t('pricing.start_free'));
        navigate('/companies');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed.');
    } finally { setPurchasing(null); }
  };

  // "Combo" > advanced > others for POPULAR badge
  const getHighlightedPackage = (name) => {
    const n = name.toLowerCase();
    return n.includes('combo');
  };
  const isBestValue = (name) => name.toLowerCase().includes('combo');

  const includedFeatures = [
    { title: t('pricing.feature.ai_analysis'), desc: t('pricing.feature.ai_analysis.desc') },
    { title: t('pricing.feature.swot'), desc: t('pricing.feature.swot.desc') },
    { title: t('pricing.feature.roadmap'), desc: t('pricing.feature.roadmap.desc') },
    { title: t('pricing.feature.benchmark'), desc: t('pricing.feature.benchmark.desc') },
    { title: t('pricing.feature.advisor'), desc: t('pricing.feature.advisor.desc') },
    { title: t('pricing.feature.pdf'), desc: t('pricing.feature.pdf.desc') },
  ];

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="light" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn-primary" data-testid="nav-dashboard">{t('nav.dashboard')}</Link>
            ) : (
              <>
                <Link to="/login" className="text-[#52525B] hover:text-[#09090B] font-medium">{t('nav.login')}</Link>
                <Link to="/register" className="btn-primary" data-testid="nav-register">{t('nav.register')}</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="py-16 px-6 border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="label-text text-[#0F1B36] mb-4">{t('pricing.label')}</p>
          <h1 className="heading-1 text-[#09090B] mb-6" data-testid="pricing-title">{t('pricing.title')}</h1>
          <p className="text-xl text-[#52525B]">{t('pricing.subtitle')}</p>
          {eventMode && (
            <div className="mt-8 inline-flex flex-col sm:flex-row items-center gap-3 bg-gradient-to-r from-[#0F1B36] to-[#1E3A8A] text-white px-6 py-4 rounded-lg" data-testid="event-mode-pricing-banner">
              <Lightning size={22} weight="fill" className="text-[#3CD9B8]" />
              <div className="text-left">
                <p className="font-bold text-sm sm:text-base">
                  {language === 'tr' ? '🎉 Etkinlik Özel — Tüm paketler ücretsiz' : '🎉 Event Special — All packages free'}
                </p>
                <p className="text-xs text-white/70 mt-0.5">
                  {language === 'tr'
                    ? 'Kayıt olan herkes platformun tüm özelliklerini sınırsız kullanır.'
                    : 'Every registered user has unlimited access to the full platform.'}
                </p>
              </div>
              <Link
                to={isAuthenticated ? '/dashboard' : '/register'}
                className="ml-0 sm:ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#3CD9B8] text-[#0F1B36] text-xs font-bold hover:opacity-90"
                data-testid="event-pricing-cta"
              >
                {isAuthenticated
                  ? (language === 'tr' ? 'Panele Git' : 'Go to Dashboard')
                  : (language === 'tr' ? 'Ücretsiz Kayıt' : 'Sign Up Free')}
                <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Legal consent (Türkiye Mesafeli Satış Yönetmeliği) */}
          {isAuthenticated && !eventMode && (
            <div id="legal-consent" className="mb-8 max-w-3xl mx-auto border border-[#E4E4E7] bg-[#FAFAFA] p-4 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-[#52525B]" data-testid="legal-consent-label">
                <input
                  type="checkbox"
                  checked={legalAccepted}
                  onChange={(e) => setLegalAccepted(e.target.checked)}
                  data-testid="legal-consent-checkbox"
                  className="mt-1 w-4 h-4 accent-[#0F1B36]"
                />
                <span>
                  <Link to="/legal/on-bilgilendirme" target="_blank" className="text-[#0F1B36] underline" data-testid="legal-on-bilg-link">{t('pricing.legal.preinfo')}</Link>
                  {' '}{t('pricing.legal.and')}{' '}
                  <Link to="/legal/mesafeli-satis" target="_blank" className="text-[#0F1B36] underline" data-testid="legal-mesafeli-link">{t('pricing.legal.distance')}</Link>
                  {' '}{t('pricing.legal.accept')}
                </span>
              </label>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12 text-[#52525B]">{t('common.loading')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => {
                const isHighlighted = getHighlightedPackage(pkg.name);
                return (
                  <div key={pkg.id}
                    className={`relative border p-8 ${isHighlighted ? 'border-[#0F1B36] border-2' : 'border-[#E4E4E7]'}`}
                    data-testid={`package-${pkg.id}`}
                  >
                    {isHighlighted && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0F1B36] text-white text-xs font-bold px-4 py-1">
                        {isBestValue(pkg.name) ? '🔥 BEST VALUE' : t('pricing.popular')}
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className="heading-4 text-[#09090B] mb-2">{pkg.name}</h3>
                      <p className="text-[#52525B] text-sm">{pkg.description}</p>
                    </div>
                    <div className="mb-6">
                      {appliedCoupon && appliedCoupon.package_id === pkg.id ? (
                        <div className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-[#52525B] line-through">${pkg.price}</span>
                            <span className="text-4xl font-black text-[#16A34A]">${appliedCoupon.final_price}</span>
                          </div>
                          <span className="text-xs font-medium text-[#16A34A]">
                            {appliedCoupon.is_free ? t('pricing.coupon.free_label') : `-$${appliedCoupon.discount_amount} ${t('pricing.coupon.with_code')} ${appliedCoupon.code}`}
                          </span>
                        </div>
                      ) : (
                        <>
                          <span className="text-4xl font-black text-[#09090B]">${pkg.price}</span>
                          {pkg.price > 0 && <span className="text-[#52525B] ml-2">{t('pricing.one_time')}</span>}
                        </>
                      )}
                    </div>
                    <ul className="space-y-3 mb-6">
                      {pkg.features?.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check size={18} weight="bold" className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                          <span className="text-[#52525B] text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Coupon input — hidden during Event Mode */}
                    {isAuthenticated && pkg.price > 0 && !eventMode && (
                      <div className="mb-4">
                        {appliedCoupon && appliedCoupon.package_id === pkg.id ? (
                          <div className="flex items-center justify-between bg-[#F0FDF4] border border-[#16A34A]/30 px-3 py-2">
                            <span className="text-sm text-[#15803D] flex items-center gap-2">
                              <Ticket size={14} weight="fill" />
                              {appliedCoupon.code}
                            </span>
                            <button
                              onClick={clearCoupon}
                              className="text-[#52525B] hover:text-[#E60000]"
                              data-testid={`coupon-clear-${pkg.id}`}
                              aria-label="remove coupon"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Input
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder={t('pricing.coupon.placeholder')}
                              className="rounded-none flex-1 h-9 text-sm font-mono"
                              data-testid={`coupon-input-${pkg.id}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleValidateCoupon(pkg)}
                              disabled={couponLoading || !couponCode.trim()}
                              className="rounded-none h-9 text-sm"
                              data-testid={`coupon-apply-${pkg.id}`}
                            >
                              {couponLoading ? '...' : t('pricing.coupon.apply')}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {eventMode ? (
                      <Button
                        onClick={() => navigate(isAuthenticated ? '/dashboard' : '/register')}
                        className={`w-full rounded-none ${isHighlighted
                          ? 'bg-[#3CD9B8] hover:bg-[#2BC0A0] text-[#0F1B36]'
                          : 'bg-white border border-[#0F1B36] text-[#0F1B36] hover:bg-[#0F1B36] hover:text-white'
                        }`}
                        data-testid={`purchase-${pkg.id}`}
                      >
                        {language === 'tr' ? '🎉 Etkinlikte Ücretsiz Aç' : '🎉 Unlock Free at Event'}
                        <ArrowRight size={16} className="ml-2" />
                      </Button>
                    ) : (
                      <Button onClick={() => handlePurchase(pkg)} disabled={purchasing === pkg.id}
                        className={`w-full rounded-none ${isHighlighted
                          ? 'bg-[#0F1B36] hover:bg-[#00227A] text-white'
                          : 'bg-white border border-[#09090B] text-[#09090B] hover:bg-[#09090B] hover:text-white'
                        }`}
                        data-testid={`purchase-${pkg.id}`}
                      >
                        {purchasing === pkg.id ? t('pricing.processing') : pkg.price === 0 ? t('pricing.start_free') : t('pricing.get_started')}
                        <ArrowRight size={16} className="ml-2" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 px-6 bg-[#F4F4F5]">
        <div className="max-w-4xl mx-auto">
          <h2 className="heading-2 text-[#09090B] text-center mb-12">{t('pricing.whats_included')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {includedFeatures.map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <Lightning size={24} className="text-[#0F1B36] flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[#09090B] mb-1">{item.title}</h3>
                  <p className="text-[#52525B] text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 border-t border-[#E4E4E7]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="heading-3 text-[#09090B] mb-4">{t('pricing.cta.title')}</h2>
          <p className="text-[#52525B] mb-8">{t('pricing.cta.desc')}</p>
          <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="cta-register">
            {t('pricing.cta.button')} <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      <footer className="py-8 px-6 border-t border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo variant="light" size="md" />
          </div>
          <p className="text-[#52525B] text-sm">{t('pricing.footer')}</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
